<template>
    <div class="team-calendar-panel">
        <div class="d-flex align-center flex-wrap mb-2" style="gap: 10px;">
            <span class="text-h6 font-weight-bold">
                <v-icon left color="primary">mdi-calendar-account</v-icon>
                {{ isTeam ? 'Team Calendar' : 'Business Unit Calendar' }}
            </span>
            <v-btn-toggle v-model="view" mandatory dense color="primary">
                <v-btn small value="month" class="text-none"><v-icon small left>mdi-calendar-month</v-icon>Calendar</v-btn>
                <v-btn small value="chart" class="text-none"><v-icon small left>mdi-chart-gantt</v-icon>Chart</v-btn>
            </v-btn-toggle>
            <v-btn outlined small color="primary" class="text-none" @click="openPeople">
                <v-icon small left>mdi-account-multiple</v-icon>People &amp; balances
            </v-btn>
            <v-spacer />
            <v-select
                v-if="companyOptions.length > 1"
                v-model="companyId"
                :items="companyOptions"
                item-text="name"
                item-value="id"
                :label="isTeam ? 'Team' : 'Company'"
                outlined
                dense
                hide-details
                style="max-width: 260px;"
            />
            <v-chip v-else-if="companyOptions.length === 1" label outlined color="primary" class="font-weight-bold">
                <v-icon left small>{{ isTeam ? 'mdi-account-group' : 'mdi-domain' }}</v-icon>
                {{ companyOptions[0].name }}
            </v-chip>
            <v-text-field
                v-model="search"
                placeholder="Search members…"
                outlined
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                style="max-width: 240px;"
            />
            <div class="d-flex align-center">
                <v-btn icon small @click="shiftMonth(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
                <span class="font-weight-bold mx-2">{{ monthLabel }}</span>
                <v-btn icon small @click="shiftMonth(1)"><v-icon>mdi-chevron-right</v-icon></v-btn>
                <v-btn outlined small class="ml-2 text-none" @click="goToday">Today</v-btn>
            </div>
        </div>

        <v-alert v-if="noCompany" type="info" dense text>
            {{ isTeam ? "You don't manage any team, so there is no team calendar to show." : "You don't manage any company, so there is no business unit calendar to show." }}
        </v-alert>
        <v-alert v-else-if="forbidden" type="warning" dense text>
            {{ isTeam ? "You don't have access to this team's calendar." : "You don't have access to this company's calendar." }}
        </v-alert>
        <v-alert v-else-if="error" type="error" dense text>
            Couldn't load the {{ isTeam ? 'team' : 'business unit' }} calendar.
            <v-btn small text class="text-none" @click="load">Retry</v-btn>
        </v-alert>
        <v-skeleton-loader v-else-if="!loaded" type="card" />

        <div v-if="loaded" class="team-counts mb-3">
            <div class="d-flex flex-wrap" style="gap: 10px;">
                <v-card v-for="c in countCards" :key="c.key" outlined class="count-card pa-3" @click="openCount(c.key)">
                    <div class="d-flex align-center">
                        <v-icon :color="c.color" class="mr-2">{{ c.icon }}</v-icon>
                        <span class="text-caption grey--text text--darken-1">{{ c.label }}</span>
                    </div>
                    <div class="text-h5 font-weight-bold">{{ c.people }}<span class="text-caption grey--text ml-1">{{ c.people === 1 ? 'person' : 'people' }}</span></div>
                    <div class="text-caption grey--text">{{ c.sub }}</div>
                    <div v-if="c.today !== null" class="text-caption">Today: <b>{{ c.today }}</b></div>
                </v-card>
            </div>
            <div v-if="counts.byType.length" class="d-flex flex-wrap align-center mt-2" style="gap: 6px;">
                <span class="text-caption grey--text mr-1">Leave by type:</span>
                <v-chip v-for="t in counts.byType" :key="t.name" small label outlined :color="colorForType(t.typeId)">
                    {{ t.name }} · {{ t.people }} {{ t.people === 1 ? 'person' : 'people' }} · {{ t.days }}d
                </v-chip>
            </div>
        </div>

        <LeaveMonthCalendar
            v-if="loaded && view === 'month'"
            :days="days"
            :buckets="dayBuckets"
            :notes="notes"
            :users="allUsers"
            :scope-label="isTeam ? 'team' : 'company'"
            :leave-types="leaveTypes"
            :color-for-type="colorForType"
            :can-add-leave="canLogLeave"
            @day-click="openDay"
            @add-leave="openManualFromDay"
        />

        <LeaveWallchartGrid
            v-if="loaded && view === 'chart'"
            :days="days"
            :rows="rows"
            :legend-types="leaveTypes"
            :status-legend="true"
            :color-for-type="colorForType"
            :empty-text="search ? 'No members match your search' : 'No members to show'"
            legend-empty-text="No leave this month"
            :notes="notes"
            :notes-enabled="true"
            :can-add-leave="canLogLeave"
            :addable-user-ids="addableIds"
            @cell-add-leave="openManualFromCell"
            @row-click="openCalendar"
            @cell-click="openDetail"
            @cell-note-click="openNotes"
            @day-note-click="openCompanyNotes"
        />

        <v-dialog v-model="countDialog" max-width="640" scrollable>
            <v-card v-if="countCard">
                <v-card-title class="d-flex align-center">
                    <v-icon :color="countCard.color" class="mr-2">{{ countCard.icon }}</v-icon>
                    {{ countCard.label }} — {{ monthLabel }}
                    <v-spacer />
                    <v-btn icon small @click="countDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pa-0">
                    <div v-if="!countItems.length" class="text-center grey--text py-8">{{ isPendingKey ? 'Nothing waiting for a decision' : 'Nobody this month' }}</div>
                    <div v-for="it in countItems" :key="it.request.id" class="count-item px-4 py-3" @click="openCountRequest(it.request)">
                        <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                            <span class="font-weight-bold">{{ it.name }}</span>
                            <v-chip x-small label outlined :color="colorForType(it.request.leave_type_id)">{{ it.typeName }}</v-chip>
                            <v-chip v-if="it.isToday" x-small color="primary" dark>Today</v-chip>
                            <v-spacer />
                            <span class="text-caption grey--text">{{ it.range }}<span v-if="it.request.is_half_day"> · half day</span></span>
                        </div>
                        <div v-if="it.request.note" class="text-body-2 mt-1 count-employee-note">
                            <v-icon x-small class="mr-1">mdi-comment-text-outline</v-icon>
                            <span class="grey--text text--darken-1">Employee note:</span> {{ it.request.note }}
                        </div>
                        <div v-if="isPendingKey" class="mt-2" @click.stop>
                            <v-textarea :value="decisionNotes[it.request.id]" outlined @input="$set(decisionNotes, it.request.id, $event)" dense rows="2" hide-details
                                placeholder="Add a note for the employee (optional)" />
                            <div class="d-flex justify-end mt-2" style="gap: 8px;">
                                <v-btn small outlined color="error" class="text-none" :loading="updatingId === it.request.id"
                                    :disabled="updatingId !== null" @click="decide(it, 3)">Decline</v-btn>
                                <v-btn small color="success" dark class="text-none" :loading="updatingId === it.request.id"
                                    :disabled="updatingId !== null" @click="decide(it, 2)">Approve</v-btn>
                            </div>
                        </div>
                        <div v-for="n in it.notes" :key="n.id" class="text-body-2 mt-1 count-note">
                            <v-icon x-small class="mr-1">mdi-note-text-outline</v-icon>
                            <span class="grey--text text--darken-1">{{ $date(n.note_date).format('MMM D') }}:</span> {{ n.body }}
                            <span v-if="n.author" class="text-caption grey--text"> — {{ n.author.name }}</span>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog v-model="peopleDialog" max-width="520" scrollable>
            <v-card>
                <v-card-title class="d-flex align-center">
                    <span>{{ companyName }} — people</span>
                    <v-spacer />
                    <v-btn icon small @click="peopleDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-subtitle class="pb-2">Open someone's yearly calendar, leave balance and entitlement</v-card-subtitle>
                <div class="px-4 pb-2">
                    <v-text-field
                        v-model="peopleQuery"
                        placeholder="Search people…"
                        outlined
                        dense
                        hide-details
                        clearable
                        prepend-inner-icon="mdi-magnify"
                    />
                </div>
                <v-divider />
                <v-card-text class="pa-0" style="max-height: 60vh;">
                    <div v-if="!peopleList.length" class="text-center grey--text py-8">No people found</div>
                    <div v-for="u in peopleList" :key="u.id" class="people-row px-4 py-2" @click="openPerson(u)">
                        <div>
                            <div class="font-weight-medium">{{ fullNameOf(u) }}</div>
                            <div class="text-caption grey--text">{{ u.position_name || u.role_name || '' }}</div>
                        </div>
                        <v-spacer />
                        <v-icon small>mdi-chevron-right</v-icon>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog v-model="calendarDialog" max-width="1180" content-class="uc-dialog-shell">
            <UserLeaveCalendar
                v-if="calendarUser"
                :key="calendarUser.id"
                :user="calendarUser"
                :requests="calendarRequests"
                :balances="calendarBalances"
                :leave-types="leaveTypes"
                :initial-year="$date(monthAnchor).year()"
                :can-edit-allowance="canEditCalendarAllowance"
                :can-add-leave="canEditCalendarAllowance"
                @edit-allowance="openAllowance"
                @add-leave="openManualLeave"
                @close="calendarDialog = false"
            />
        </v-dialog>

        <ManagerLeaveDialog
            v-model="manualDialog"
            :user="manualUser"
            :people="addablePeople"
            :date="manualDate"
            :leave-types="leaveTypes"
            :saving="manualSaving"
            :note-scope="isTeam ? { role_id: companyId } : { company_id: companyId }"
            @save="saveManualLeave"
        />

        <LeaveAllowanceDialog
            v-model="allowanceDialog"
            :user="calendarUser"
            :year="allowanceYear"
            :leave-types="leaveTypes"
            :counts="calendarCounts"
            :saving="allowanceSaving"
            @save="saveAllowance"
        />

        <LeaveDetailModal
            :request="detailDialog ? selectedRequest : null"
            :leave-types="leaveTypes"
            :users="allUsers"
            @close="detailDialog = false"
        />

        <LeaveDayDialog
            v-model="dayDialog"
            :day="dayInfo"
            :bucket="dayBucket"
            :notes="dayNotes"
            :users="allUsers"
            :leave-types="leaveTypes"
            :color-for-type="colorForType"
            :scope-label="isTeam ? 'team' : 'company'"
            :updating-id="updatingId"
            :can-add-leave="canLogLeave"
            @add-leave="openManualFromDay"
            @decide="decideFromDay"
            @open-request="openRequestFromDay"
            @person-note="personNoteFromDay"
            @company-note="companyNoteFromDay"
        />

        <LeaveNoteDialog
            ref="noteDialog"
            v-model="noteDialog"
            :title="noteTarget ? noteTarget.title : ''"
            :subtitle="noteTarget ? noteTarget.subtitle : ''"
            :notes="targetNotes"
            :current-user-id="meId"
            :is-super-admin="isSuperAdminViewer"
            :busy="noteBusy"
            :leave="noteTarget ? noteTarget.leave : null"
            @add="addNote"
            @edit="editNote"
            @remove="removeNote"
            @open-request="openRequestFromNote"
        />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import LeaveWallchartGrid from './LeaveWallchartGrid.vue';
import UserLeaveCalendar from './UserLeaveCalendar.vue';
import LeaveDetailModal from './LeaveDetailModal.vue';
import LeaveNoteDialog from './LeaveNoteDialog.vue';
import LeaveMonthCalendar from './LeaveMonthCalendar.vue';
import LeaveDayDialog from './LeaveDayDialog.vue';
import LeaveAllowanceDialog from './LeaveAllowanceDialog.vue';
import ManagerLeaveDialog from './ManagerLeaveDialog.vue';
import {
    buildDays,
    buildWallchartRows,
    colorForType,
    fullName,
    requestUserId,
    isLeaveSuperAdmin,
    managedCompanyIds,
    managedTeamIds,
    summarizeTeamCounts,
    buildDayBuckets,
    isWfhRequest,
    requestStatusId,
    APPROVED_STATUS_ID,
    PENDING_STATUS_ID,
} from './leaveUtils';

export default {
    name: 'TeamCalendarPanel',
    components: { LeaveWallchartGrid, UserLeaveCalendar, LeaveDetailModal, LeaveNoteDialog, LeaveMonthCalendar, LeaveDayDialog, LeaveAllowanceDialog, ManagerLeaveDialog },
    props: {
        // 'company' = Business Unit Calendar, 'team' = Team (user role) Calendar.
        // Everything below named company* is the selected company or team id.
        scope: { type: String, default: 'company' },
    },
    inject: ['users', 'companies', 'roleManagers', 'refreshApprovalCounts'],
    data() {
        return {
            companyId: null,
            monthAnchor: this.$date().startOf('month').format('YYYY-MM-DD'),
            search: '',
            loading: false,
            loaded: false,
            error: false,
            forbidden: false,
            memberIds: [],
            requests: [],
            leaveTypes: [],
            loadSeq: 0,
            calendarDialog: false,
            calendarUser: null,
            calendarRequests: [], // the person's whole history (not just the visible month)
            calendarBalances: [], // [{ id, get_leaves }] -> allowance panel in the dialog
            calendarSeq: 0,
            peopleDialog: false,
            peopleQuery: '',
            allowanceDialog: false,
            allowanceYear: new Date().getFullYear(),
            allowanceSaving: false,
            manualDialog: false,
            manualDate: null,
            manualUser: null, // fixed person, or null to let the dialog ask
            manualSaving: false,
            detailDialog: false,
            selectedRequest: null,
            notes: [],
            noteDialog: false,
            noteTarget: null,
            noteBusy: false,
            noteSeq: 0,
            countDialog: false,
            countKey: null,
            view: 'month', // 'month' (month-grid calendar, default) | 'chart' (wallchart)
            dayDialog: false,
            dayInfo: null,
            decisionNotes: {},
            updatingId: null,
        };
    },
    computed: {
        ...mapGetters({ me: 'auth/get_user', get_roles: 'auth/get_roles' }),
        isTeam() {
            return this.scope === 'team';
        },
        apiBase() {
            return this.isTeam
                ? { calendar: 'team_calendar', notes: 'team_notes' }
                : { calendar: 'company_calendar', notes: 'company_notes' };
        },
        allUsers() {
            return this.users() || [];
        },
        // Super Admin sees every company; everyone else (including role 2/11)
        // only the companies they are marked as manager of.
        companyOptions() {
            if (this.isTeam) {
                const roles = this.get_roles || [];
                const managed = managedTeamIds(this.me, this.roleManagers ? this.roleManagers() : []);
                return roles
                    .filter((r) => isLeaveSuperAdmin(this.me) || managed.has(Number(r.id)))
                    .map((r) => ({ id: Number(r.id), name: r.name }))
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            }
            const all = (this.companies && this.companies()) || [];
            let list;
            if (isLeaveSuperAdmin(this.me)) {
                list = all.map((c) => ({ id: Number(c.id), name: c.name }));
            } else {
                const viewer = this.allUsers.find((u) => Number(u.id) === Number(this.me && this.me.id));
                const managed = managedCompanyIds(viewer);
                list = all
                    .filter((c) => managed.has(Number(c.id)))
                    .map((c) => ({ id: Number(c.id), name: c.name }));
            }
            return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        },
        companyName() {
            const c = this.companyOptions.find((o) => o.id === this.companyId);
            return c ? c.name : (this.isTeam ? 'Team' : 'Company');
        },
        meId() {
            const id = Number(this.me && this.me.id);
            return Number.isFinite(id) ? id : null;
        },
        isSuperAdminViewer() {
            return isLeaveSuperAdmin(this.me);
        },
        targetNotes() {
            if (!this.noteTarget) return [];
            const uid = this.noteTarget.userId;
            return this.notes.filter(
                (n) => (Number(n.user_id) || null) === uid && (n.note_date || '').substring(0, 10) === this.noteTarget.date
            );
        },
        noCompany() {
            return this.companyOptions.length === 0;
        },
        monthLabel() {
            return this.$date(this.monthAnchor).format('MMMM YYYY');
        },
        monthStart() {
            return this.$date(this.monthAnchor).startOf('month').format('YYYY-MM-DD');
        },
        monthEnd() {
            return this.$date(this.monthAnchor).endOf('month').format('YYYY-MM-DD');
        },
        days() {
            const count = this.$date(this.monthEnd).date();
            return buildDays(this.monthStart, count, this.$date().format('YYYY-MM-DD'));
        },
        members() {
            const ids = new Set(this.memberIds);
            const q = (this.search || '').trim().toLowerCase();
            return this.allUsers
                .filter((u) => ids.has(Number(u.id)) && !u.deleted_at)
                .map((u) => ({ ...u, role_name: (u.role && u.role.name) || u.role_name }))
                .filter((u) => !q || fullName(u).toLowerCase().includes(q))
                .sort((a, b) => fullName(a).localeCompare(fullName(b)));
        },
        rows() {
            return buildWallchartRows({
                users: this.members,
                requests: this.requests,
                days: this.days,
                leaveTypes: this.leaveTypes,
                includePending: true,
            });
        },
        counts() {
            return summarizeTeamCounts({
                requests: this.requests,
                memberIds: this.memberIds,
                from: this.monthStart,
                to: this.monthEnd,
                leaveTypes: this.leaveTypes,
            });
        },
        todayCounts() {
            const t = this.$date().format('YYYY-MM-DD');
            if (t < this.monthStart || t > this.monthEnd) return null;
            return summarizeTeamCounts({
                requests: this.requests,
                memberIds: this.memberIds,
                from: t,
                to: t,
                leaveTypes: this.leaveTypes,
            });
        },
        countCards() {
            const c = this.counts;
            const td = this.todayCounts;
            const pend = (wfh) => {
                const list = this.pendingRequests(wfh);
                return { people: new Set(list.map((r) => Number(r.user_id))).size, n: list.length };
            };
            const pl = pend(false);
            const pw = pend(true);
            const reqs = (n) => `${n} request${n === 1 ? '' : 's'} awaiting a decision`;
            const days = (n) => `${n} person-day${n === 1 ? '' : 's'}`;
            return [
                { key: 'leave', label: 'On leave', icon: 'mdi-beach', color: '#c2185b', people: c.leave.people, sub: days(c.leave.days), today: td ? td.leave.people : null },
                { key: 'wfh', label: 'Work from home', icon: 'mdi-home-account', color: '#2e7d32', people: c.wfh.people, sub: days(c.wfh.days), today: td ? td.wfh.people : null },
                { key: 'pendingLeave', label: 'Pending leave approvals', icon: 'mdi-calendar-clock', color: '#ef6c00', people: pl.people, sub: reqs(pl.n), today: null },
                { key: 'pendingWfh', label: 'Pending WFH approvals', icon: 'mdi-home-clock', color: '#ef6c00', people: pw.people, sub: reqs(pw.n), today: null },
            ];
        },
        isPendingKey() {
            return this.countKey === 'pendingLeave' || this.countKey === 'pendingWfh';
        },
        // the person's per-year entitlement rows (feeds the allowance panel and its editor)
        calendarCounts() {
            const rec = this.calendarBalances[0];
            return (rec && rec.get_leaves) || [];
        },
        // Super Admin: anyone. Company / team managers: the members listed here (the API
        // enforces the scope), but never themselves.
        canEditCalendarAllowance() {
            if (!this.calendarUser) return false;
            return isLeaveSuperAdmin(this.me) || Number(this.calendarUser.id) !== this.meId;
        },
        // who a manager may log leave for here: the members, never themselves (Super Admin: anyone)
        addablePeople() {
            return this.peopleList.filter((u) => this.isSuperAdminViewer || Number(u.id) !== this.meId);
        },
        addableIds() {
            return this.addablePeople.map((u) => Number(u.id));
        },
        canLogLeave() {
            return this.addablePeople.length > 0;
        },
        // everyone in the selected company / team, A-Z (not affected by the chart's search box)
        peopleList() {
            const ids = new Set(this.memberIds);
            const q = (this.peopleQuery || '').trim().toLowerCase();
            return this.allUsers
                .filter((u) => ids.has(Number(u.id)) && !u.deleted_at)
                .map((u) => ({ ...u, role_name: (u.role && u.role.name) || u.role_name }))
                .filter((u) => !q || fullName(u).toLowerCase().includes(q))
                .sort((a, b) => fullName(a).localeCompare(fullName(b)));
        },
        dayBuckets() {
            return buildDayBuckets({
                requests: this.requests,
                memberIds: this.memberIds,
                days: this.days,
                leaveTypes: this.leaveTypes,
            });
        },
        dayBucket() {
            return this.dayInfo ? this.dayBuckets[this.dayInfo.key] || null : null;
        },
        dayNotes() {
            if (!this.dayInfo) return [];
            return this.notes.filter((n) => (n.note_date || '').substring(0, 10) === this.dayInfo.key);
        },
        countCard() {
            return this.countCards.find((c) => c.key === this.countKey) || null;
        },
        // approved requests for the open count card (leave or WFH), with the
        // managers' notes dated inside each request's range
        countItems() {
            if (!this.countKey) return [];
            const wantWfh = this.countKey === 'wfh' || this.countKey === 'pendingWfh';
            const today = this.$date().format('YYYY-MM-DD');
            const source = this.isPendingKey ? this.pendingRequests(wantWfh) : this.approvedRequests(wantWfh);
            return source
                .map((r) => {
                    const f = (r.from || '').substring(0, 10);
                    const t = (r.to || '').substring(0, 10);
                    const u = this.allUsers.find((x) => Number(x.id) === Number(r.user_id));
                    const lt = this.leaveTypes.find((x) => x.id === r.leave_type_id) || r.leave_type || {};
                    return {
                        request: r,
                        name: u ? fullName(u) : 'Unknown user',
                        typeName: lt.name || 'Leave',
                        range: f === t ? this.$date(f).format('ddd, MMM D') : `${this.$date(f).format('MMM D')} – ${this.$date(t).format('MMM D')}`,
                        isToday: f <= today && today <= t,
                        notes: this.notes.filter((n) => {
                            const d = (n.note_date || '').substring(0, 10);
                            return Number(n.user_id) === Number(r.user_id) && d >= f && d <= t;
                        }),
                    };
                })
                .sort((a, b) => a.request.from.localeCompare(b.request.from) || a.name.localeCompare(b.name));
        },
    },
    watch: {
        companyOptions: {
            immediate: true,
            handler(opts) {
                // pick a default once companies/users have loaded; keep a valid choice
                if (!opts.length) {
                    this.companyId = null;
                    return;
                }
                if (!opts.some((o) => o.id === this.companyId)) this.companyId = opts[0].id;
            },
        },
        companyId() {
            this.load();
        },
        monthAnchor() {
            this.load();
        },
        calendarDialog(open) {
            // v-dialog: push it below the sticky nav (which renders above dialogs)
            if (!open) return;
            this.$nextTick(() => {
                const dlg = document.querySelector('.v-dialog.uc-dialog-shell');
                if (!dlg) return;
                const nav = document.querySelector('.top-navigation');
                const top = nav ? nav.getBoundingClientRect().bottom : 120;
                dlg.style.marginTop = `${Math.max(24, Math.round(top) + 12)}px`;
            });
        },
    },
    mounted() {
        // The immediate companyOptions watcher picks the default company before the
        // companyId watcher exists, so that change never triggers a load by itself.
        if (this.companyId) this.load();
    },
    methods: {
        colorForType(id) {
            return colorForType(id, this.leaveTypes);
        },
        shiftMonth(dir) {
            this.monthAnchor = this.$date(this.monthAnchor).add(dir, 'month').startOf('month').format('YYYY-MM-DD');
        },
        goToday() {
            this.monthAnchor = this.$date().startOf('month').format('YYYY-MM-DD');
        },
        approvedRequests(wfh) {
            const ids = new Set(this.memberIds);
            return this.requests.filter(
                (r) => requestStatusId(r) === APPROVED_STATUS_ID && ids.has(Number(r.user_id)) && isWfhRequest(r, this.leaveTypes) === wfh
            );
        },
        pendingRequests(wfh) {
            const ids = new Set(this.memberIds);
            return this.requests.filter(
                (r) => requestStatusId(r) === PENDING_STATUS_ID && ids.has(Number(r.user_id)) && isWfhRequest(r, this.leaveTypes) === wfh
            );
        },
        async decide(item, statusId) {
            const r = item.request;
            this.updatingId = r.id;
            try {
                const res = await this.$axios.patch(`/api/leave/approval/${r.id}`, {
                    leave_status_id: statusId,
                    is_updated: 1,
                    user_id: r.user_id,
                    count: r.count,
                    reason: this.decisionNotes[r.id] || null,
                });
                if (!res || res.status < 200 || res.status >= 300 || (res.data && res.data.response === false)) {
                    this.$toast.error((res && res.data && res.data.message) || 'Failed to update the request');
                    return;
                }
                this.$toast.success(`Request ${statusId === APPROVED_STATUS_ID ? 'approved' : 'declined'}`);
                this.$delete(this.decisionNotes, r.id);
                await this.load();
                if (typeof this.refreshApprovalCounts === 'function') this.refreshApprovalCounts();
            } catch (e) {
                this.$toast.error('Failed to update the request');
            } finally {
                this.updatingId = null;
            }
        },
        openDay(day) {
            this.dayInfo = day;
            this.dayDialog = true;
        },
        async decideFromDay({ request, statusId, note }) {
            this.$set(this.decisionNotes, request.id, note);
            await this.decide({ request }, statusId);
        },
        openRequestFromDay(request) {
            this.dayDialog = false;
            this.openDetail(request);
        },
        personNoteFromDay({ userId, request }) {
            const user = this.allUsers.find((u) => Number(u.id) === Number(userId));
            if (!user || !this.dayInfo) return;
            this.dayDialog = false;
            this.openNotes({ user, day: this.dayInfo, leave: request });
        },
        companyNoteFromDay() {
            if (!this.dayInfo) return;
            this.dayDialog = false;
            this.openCompanyNotes(this.dayInfo);
        },
        openCount(key) {
            this.countKey = key;
            this.countDialog = true;
        },
        openCountRequest(request) {
            this.countDialog = false;
            this.openDetail(request);
        },
        openPeople() {
            this.peopleQuery = '';
            this.peopleDialog = true;
        },
        openPerson(user) {
            this.peopleDialog = false;
            this.openCalendar(user);
        },
        fullNameOf(u) {
            return fullName(u);
        },
        openAllowance(year) {
            this.allowanceYear = Number(year);
            this.allowanceDialog = true;
        },
        // from the open person calendar: that person is fixed
        openManualLeave(date) {
            this.manualUser = this.calendarUser;
            this.manualDate = date || null;
            this.manualDialog = true;
        },
        // from a chart cell: the row's person and the cell's day
        openManualFromCell({ user, day }) {
            this.manualUser = user;
            this.manualDate = day.key;
            this.manualDialog = true;
        },
        // from a month-calendar day: the dialog asks who
        openManualFromDay(day) {
            this.manualUser = null;
            this.manualDate = day.key;
            this.manualDialog = true;
        },
        // A manager logs leave for the person whose calendar is open; it is approved on the spot.
        async saveManualLeave(payload) {
            if (!payload || !payload.user_id) return;
            this.manualSaving = true;
            try {
                const uid = Number(payload.user_id);
                const res = await this.$axios.post(`/api/leave/manual/${uid}`, payload);
                if (res && res.status >= 200 && res.status < 300 && res.data && res.data.response) {
                    const added = { ...res.data.request, user_id: uid };
                    this.requests = [...this.requests, added];
                    if (this.calendarUser && Number(this.calendarUser.id) === uid) {
                        this.calendarRequests = [...this.calendarRequests, added];
                        this.calendarBalances = [{ id: uid, get_leaves: res.data.leave_counts || [] }];
                    }
                    this.manualDialog = false;
                    // a private manager note was saved alongside: pull it in so it shows straight away
                    if (payload.manager_note) this.loadNotes();
                    this.$toast.success('Leave added');
                } else {
                    const d = (res && res.data) || {};
                    const first = d.errors ? Object.values(d.errors)[0] : null;
                    this.$toast.error(d.message || (Array.isArray(first) ? first[0] : first) || 'Couldn’t add the leave');
                }
            } finally {
                this.manualSaving = false;
            }
        },
        async saveAllowance({ year, items }) {
            if (!this.calendarUser) return;
            this.allowanceSaving = true;
            try {
                const res = await this.$axios.put(`/api/leave/user_allowance/${this.calendarUser.id}`, { year, items });
                if (res && res.status >= 200 && res.status < 300 && res.data && res.data.response) {
                    this.calendarBalances = [{ id: this.calendarUser.id, get_leaves: res.data.leave_counts || [] }];
                    this.allowanceDialog = false;
                    this.$toast.success('Leave entitlement updated');
                } else {
                    const d = (res && res.data) || {};
                    const first = d.errors ? Object.values(d.errors)[0] : null;
                    this.$toast.error(d.message || (Array.isArray(first) ? first[0] : first) || 'Couldn’t update the entitlement');
                }
            } finally {
                this.allowanceSaving = false;
            }
        },
        async openCalendar(user) {
            const seq = ++this.calendarSeq;
            this.calendarUser = user;
            // show what we already have for the month right away, then load the full year(s)
            this.calendarRequests = this.requests.filter((r) => Number(r.user_id) === Number(user.id));
            this.calendarBalances = [];
            this.calendarDialog = true;
            const res = await this.$axios.get(`/api/leave/user_calendar/${user.id}`);
            if (seq !== this.calendarSeq) return; // another person was opened meanwhile
            if (res && Array.isArray(res.data && res.data.requests)) {
                this.calendarRequests = res.data.requests.map((r) => ({ ...r, user_id: Number(requestUserId(r)) }));
                this.calendarBalances = [{ id: user.id, get_leaves: res.data.leave_counts || [] }];
            } else if (res && res.status !== 403) {
                this.$toast.error('Couldn’t load this person’s full calendar');
            }
        },
        openDetail(request) {
            this.selectedRequest = request;
            this.detailDialog = true;
        },
        openNotes({ user, day, leave }) {
            this.noteTarget = {
                userId: Number(user.id),
                date: day.key,
                title: fullName(user),
                subtitle: this.$date(day.key).format('dddd, MMM D, YYYY'),
                leave,
            };
            this.noteDialog = true;
        },
        openCompanyNotes(day) {
            this.noteTarget = {
                userId: null,
                date: day.key,
                title: `${this.companyName} — whole ${this.isTeam ? 'team' : 'company'}`,
                subtitle: this.$date(day.key).format('dddd, MMM D, YYYY'),
                leave: null,
            };
            this.noteDialog = true;
        },
        openRequestFromNote(leave) {
            this.noteDialog = false;
            this.openDetail(leave);
        },
        async loadNotes() {
            if (!this.companyId) return;
            const seq = ++this.noteSeq;
            const res = await this.$axios.get(`/api/leave/${this.apiBase.notes}/${this.companyId}`, {
                from: this.monthStart,
                to: this.monthEnd,
            });
            if (seq !== this.noteSeq) return; // superseded by a newer load / company / month change
            if (res && Array.isArray(res.data && res.data.notes)) {
                this.notes = res.data.notes;
            } else {
                this.notes = [];
                if (res && res.status !== 403) this.$toast.error('Couldn’t load notes');
            }
        },
        noteErrorMessage(r) {
            const d = r && r.data;
            let msg = d && d.message;
            if (!msg && d && d.errors) {
                const first = Object.values(d.errors || {})[0];
                msg = Array.isArray(first) ? first[0] : first;
            }
            return (typeof msg === 'string' && msg) || (r && typeof r.message === 'string' && r.message) || 'Couldn’t save the note';
        },
        async noteRequest(fn) {
            this.noteBusy = true;
            try {
                const res = await fn();
                const ok = res && res.status >= 200 && res.status < 300;
                if (!ok) {
                    this.$toast.error(this.noteErrorMessage(res));
                    return false;
                }
                await this.loadNotes();
                return true;
            } catch (e) {
                // $axios.delete rejects on HTTP/network errors (response-like object or Error)
                this.$toast.error(this.noteErrorMessage(e));
                return false;
            } finally {
                this.noteBusy = false;
            }
        },
        async addNote(body) {
            const t = this.noteTarget;
            const ok = await this.noteRequest(() =>
                this.$axios.post(`/api/leave/${this.apiBase.notes}/${this.companyId}`, { user_id: t.userId, note_date: t.date, body })
            );
            if (ok && this.$refs.noteDialog) this.$refs.noteDialog.clearDraft();
        },
        async editNote({ id, body }) {
            const ok = await this.noteRequest(() => this.$axios.patch(`/api/leave/company_notes/${id}`, { body }));
            if (ok && this.$refs.noteDialog) this.$refs.noteDialog.stopEditing();
        },
        async removeNote(id) {
            await this.noteRequest(() => this.$axios.delete(`/api/leave/company_notes/${id}`));
        },
        async load() {
            if (!this.companyId) return;
            const seq = ++this.loadSeq;
            this.noteSeq++;
            this.notes = [];
            this.loading = true;
            this.error = false;
            this.forbidden = false;
            try {
                const [cal, types] = await Promise.all([
                    // axios plugin: pass the query object directly (it sets config.params itself)
                    this.$axios.get(`/api/leave/${this.apiBase.calendar}/${this.companyId}`, {
                        from: this.monthStart,
                        to: this.monthEnd,
                    }),
                    this.leaveTypes.length ? Promise.resolve(null) : this.$axios.get('/api/leave/type'),
                ]);
                if (seq !== this.loadSeq) return; // a newer load superseded this one
                if (cal && cal.status === 403) {
                    this.forbidden = true;
                    this.loaded = false;
                    return;
                }
                if (!cal || !Array.isArray(cal.data && cal.data.member_ids)) {
                    this.error = true;
                    this.loaded = false;
                    return;
                }
                this.memberIds = cal.data.member_ids.map(Number);
                this.requests = (cal.data.requests || []).map((r) => ({ ...r, user_id: Number(requestUserId(r)) }));
                if (types && types.data) this.leaveTypes = Array.isArray(types.data) ? types.data : (types.data.data || []);
                this.loaded = true;
                this.loadNotes();
            } catch (e) {
                if (seq === this.loadSeq) {
                    this.error = true;
                    this.loaded = false;
                }
            } finally {
                if (seq === this.loadSeq) this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.people-row {
    display: flex;
    align-items: center;
    cursor: pointer;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.people-row:hover {
    background: rgba(0, 0, 0, 0.04);
}
.count-card {
    min-width: 170px;
    cursor: pointer;
}
.count-item {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    cursor: pointer;
}
.count-item:hover {
    background: rgba(0, 0, 0, 0.03);
}
.count-employee-note {
    font-style: italic;
    white-space: pre-line;
}
.count-note {
    padding-left: 2px;
}
</style>
